import { h, FunctionComponent } from 'preact';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'preact/hooks';
import {
  RatingGroup,
  StarButton,
  VisuallyHidden,
  EmptyStarIcon,
  FullStarIcon,
} from './Rating.styles';
import { RatingProps } from './Rating.types';
import {
  focusStarButton,
  getStarLabel,
  isStarSelected,
  isValidRating,
} from './Rating.utils';
import emptyStarIcon from '../../icons/empty-star-icon.svg';
import fullStarIcon from '../../icons/full-star-icon.svg';

const DefaultEmptyStar = ({ iconSize }: { iconSize: number }) => (
  <EmptyStarIcon src={emptyStarIcon} iconSize={iconSize} aria-hidden />
);

const DefaultFullStar = ({ iconSize }: { iconSize: number }) => (
  <FullStarIcon src={fullStarIcon} iconSize={iconSize} aria-hidden />
);

/**
 * Accessible Rating component with keyboard navigation
 * - TAB: Tab to seletable star. If has rating, tab to current rating. If no rating, tab to 1st star.
 * - SHIFT+TAB: Same as TAB behavior
 * - ARROW LEFT/RIGHT: Move focus between stars (with circular navigation)
 * - SPACE/ENTER: Select the focused star
 * - 1-5 KEYS: Directly select rating
 */
const Rating: FunctionComponent<RatingProps> = ({
  name = 'rating',
  value = 0,
  defaultValue = 0,
  length = 5,
  isReadOnly = false,
  onChange,
  className,
  labels,
  ariaLabelledBy,
  iconSize = 29,
}) => {
  // Refs
  const containerRef = useRef<HTMLDivElement>(null);

  // State
  const [internalValue, setInternalValue] = useState(
    isValidRating(value) ? value : defaultValue,
  );
  const [focusedValue, setFocusedValue] = useState<number | null>(null);

  // Sync internal value with prop value if it's a controlled component
  useEffect(() => {
    if (isValidRating(value)) {
      setInternalValue(value);
    }
  }, [value]);

  // Event handlers
  const handleChange = useCallback(
    (newValue: number) => {
      if (isReadOnly) return;

      setInternalValue(newValue);
      if (onChange) {
        onChange(newValue);
      }
    },
    [isReadOnly, onChange],
  );

  const handleFocus = useCallback((rating: number) => {
    setFocusedValue(rating);
  }, []);

  const handleBlur = useCallback(() => {
    setFocusedValue(null);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: any) => {
      if (isReadOnly) return;

      const { key } = event;
      const currentValue = focusedValue !== null ? focusedValue : 1;

      switch (key) {
        case 'ArrowLeft':
        case 'ArrowUp': {
          // If at first, wrap to last, otherwise move to previous
          const prevValue = currentValue > 1 ? currentValue - 1 : length;
          focusStarButton(containerRef.current, name, prevValue);
          event.preventDefault();
          break;
        }
        case 'ArrowRight':
        case 'ArrowDown': {
          // If at last, wrap to first, otherwise move to next
          const nextValue = currentValue < length ? currentValue + 1 : 1;
          focusStarButton(containerRef.current, name, nextValue);
          event.preventDefault();
          break;
        }
        case '1':
        case '2':
        case '3':
        case '4':
        case '5': {
          const numValue = parseInt(key, 10);
          if (numValue <= length) {
            focusStarButton(containerRef.current, name, numValue);
            event.preventDefault();
          }
          break;
        }
        default:
          break;
      }
    },
    [focusedValue, isReadOnly, length, name],
  );

  const stars = useMemo(() => {
    return Array.from({ length }, (_, index) => {
      const rating = index + 1;
      const selected = isStarSelected(index, internalValue);
      const label = getStarLabel(index, labels);

      return (
        <StarButton
          key={rating}
          id={`${name}-${rating}`}
          type="button"
          disabled={isReadOnly}
          role="radio"
          aria-checked={selected}
          aria-label={label}
          aria-posinset={rating}
          aria-setsize={length}
          onFocus={() => handleFocus(rating)}
          onBlur={handleBlur}
          onClick={() => handleChange(rating)}
          data-testid={`${name}-${rating}`}
          data-focused={focusedValue === rating}
          tabIndex={rating === (internalValue || 1) ? 0 : -1}
        >
          {selected ? (
            <DefaultFullStar iconSize={iconSize} />
          ) : (
            <DefaultEmptyStar iconSize={iconSize} />
          )}
          <VisuallyHidden>{label}</VisuallyHidden>
        </StarButton>
      );
    });
  }, [
    length,
    name,
    isReadOnly,
    internalValue,
    focusedValue,
    handleFocus,
    handleBlur,
    handleChange,
    iconSize,
    labels,
  ]);

  return (
    <RatingGroup
      className={className}
      ref={containerRef}
      role="radiogroup"
      aria-labelledby={ariaLabelledBy}
      onKeyDown={handleKeyDown}
    >
      {stars}
    </RatingGroup>
  );
};

export default Rating;
